import { all, call, cancelled, put, select, take, takeEvery } from 'redux-saga/effects';

import { GeneratorResult, SagaResult, takeLeadingUnless } from '@pressreader/appstore';
import { authInitialized, selectUser, userChangeDone } from '@pressreader/authentication-core';
import {
    getBundleOrders,
    getCurrentSubscription,
    loadTrialEligibility,
    mapEligibilityDto,
    mapFromSubscriptionProductDto,
} from '@pressreader/commerce-api';
import { ITrialEligibility } from '@pressreader/commerce-types';
import { error } from '@pressreader/logger';
import { purchaseDone } from '@pressreader/purchase';
import { ExtractReturnType } from '@pressreader/types';

import {
    loadSubscriptionDone,
    loadTrialEligibilityAction,
    loadTrialEligibilityBeganEvent,
    loadTrialEligibilityCancelledEvent,
    loadTrialEligibilityDoneEvent,
    loadTrialEligibilityFailedEvent,
} from './actions';
import { getTrialEligibility } from './selectors';

function* loadSubscription(): GeneratorResult<void> {
    try {
        const user: ReturnType<typeof selectUser> = yield select(selectUser);
        if (!user.isAuthenticated) {
            return;
        }

        const currentSubscriptionDto: ExtractReturnType<typeof getCurrentSubscription> = yield call(getCurrentSubscription);
        const bundles: ExtractReturnType<typeof getBundleOrders> = yield call(getBundleOrders);

        yield put(
            loadSubscriptionDone({
                currentSubscription: mapFromSubscriptionProductDto(currentSubscriptionDto),
                activeBundles: bundles?.filter(el => el.status === 1),
            }),
        );
    } catch (e) {
        error(e);
    }
}

function* loadTrialEligibilitySaga(): SagaResult<void> {
    try {
        const trialEligibility: ReturnType<typeof getTrialEligibility> = yield select(getTrialEligibility);
        if (trialEligibility) {
            yield put(loadTrialEligibilityCancelledEvent());
            return;
        }

        yield put(loadTrialEligibilityBeganEvent());

        const eligibility: ExtractReturnType<typeof loadTrialEligibility> = yield call(() => loadTrialEligibility());

        yield put(loadTrialEligibilityDoneEvent({ trialEligibility: mapEligibilityDto(eligibility) }));
    } catch (ex) {
        error(ex);
        yield put(loadTrialEligibilityFailedEvent());
    } finally {
        if (yield cancelled()) {
            yield put(loadTrialEligibilityFailedEvent());
        }
    }
}

function* ensureTrialEligibilityLoaded(): SagaResult<ITrialEligibility | null> {
    const trialEligibility: ReturnType<typeof getTrialEligibility> = yield select(getTrialEligibility);
    if (trialEligibility) {
        return trialEligibility;
    }

    yield put(loadTrialEligibilityAction());
    yield take([loadTrialEligibilityDoneEvent, loadTrialEligibilityFailedEvent, loadTrialEligibilityCancelledEvent]);

    return yield select(getTrialEligibility);
}

function* saga() {
    yield all([
        takeEvery([authInitialized, purchaseDone, userChangeDone], loadSubscription),
        takeLeadingUnless(loadTrialEligibilityAction, loadTrialEligibilitySaga, [take(userChangeDone)]),
    ]);
}

export { ensureTrialEligibilityLoaded, saga };
